import * as Utils from '@js/modules/utils';
import I18n from '@js/modules/translations';

import api from '@js/actions/api';

import MapInterface from '@js/modules/maps/mapInterface';
import Polyline from '@js/modules/maps/polyline';
import {DefaultRideType} from '@js/components/modules/constants';


const RideTrackMapModule = {
    _rideTrackModel: {
        // Rides properties
        ridesData: [],

        showRideMarker: false,
        onlyOneTrack: false,
        hasRideData: false,
        useSecondaryLine: false,

        rideId: undefined,

        // Group Ride layers
        // Shapes:
        // - rideId
        // - polylineSourceId
        // - endPoint
        // - linePoints
        rideFeatures: [],

        updatedRideIds: [],

        // Minimum zoom to show direction arrows
        minZoomDirectionArrow: 16,
        // Direction arrows for each ride id
        directionArrows: {}
    },

    plugRidesTracks() {
        this._onRideTrackZoomChange();
    },

    displayRideTrack(rideId, callback, {
        showRideMarker = false,
        removeIfPresent = false,
        onlyOneTrack = false,
        useSecondaryLine = false
    } = {}) {
        if (removeIfPresent) {
            if (this._rideTrackModel.rideFeatures.map((feature) => feature.rideId)
                .includes(rideId)) {
                this._removeRideTracks(rideId);

                return;
            }
        }

        if (onlyOneTrack) {
            this._rideTrackModel.onlyOneTrack = true;
            this._rideTrackModel.rideId = rideId;

            this.clearRidesTracks();
        }

        if (showRideMarker) {
            this._rideTrackModel.showRideMarker = true;
        }

        if (useSecondaryLine) {
            this._rideTrackModel.useSecondaryLine = true;
        }

        // // Do not try to display full track if zoom is not enough
        // if (this._model.map.getZoom() < this._model.zoomPolyline) {
        //     return;
        // }

        // Ensure view is set
        setTimeout(() => {
            // Load ride with all track coordinates
            this._fetchRidesTracks(
                {
                    rideIds: [rideId],
                    update: true
                },
                () => {
                    if (callback) {
                        callback();
                    }
                });
        }, 200);
    },

    extendRideTrackBounds(bounds) {
        // Fit to polylines or clusters
        if (this._rideTrackModel.rideFeatures.length > 0) {
            for (let rideIndex = 0; rideIndex < this._rideTrackModel.rideFeatures.length; rideIndex++) {
                this._rideTrackModel.rideFeatures[rideIndex].linePoints.forEach((point) => {
                    bounds = bounds.extend([point.latLng.lng, point.latLng.lat]);
                });
            }
        }

        return bounds;
    },

    serializeRidePoints() {
        if (!this._rideTrackModel.onlyOneTrack || !this._rideTrackModel.rideFeatures[0]) {
            return '';
        }

        // output format:
        let serializeString = '';

        const linePoints = this._rideTrackModel.rideFeatures[0].linePoints;

        for (let i = 0; i < linePoints.length; i++) {
            const currentLinePoint = linePoints[i];
            let deltaDistance = 0;
            // const deltaTime = currentLinePoint.deltaTime;
            let prevLinePoint;
            if (i !== 0) {
                prevLinePoint = linePoints[i - 1];
                if (prevLinePoint.deltaPause === 0) {
                    deltaDistance = currentLinePoint.deltaDistance || this.distHaversine(linePoints[i - 1].latLng, currentLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                }
            }

            let pointInfoType;
            if (i === 0) {
                pointInfoType = 'start';
            } else if (i === linePoints.length - 1) {
                pointInfoType = 'end';
            } else if (currentLinePoint.computed) {
                pointInfoType = 'auto';
            } else {
                pointInfoType = 'manual';
            }

            serializeString += [
                pointInfoType,
                parseFloat(currentLinePoint.latLng.lat.toFixed(MapInterface.COORDINATES_PRECISION)),
                parseFloat(currentLinePoint.latLng.lng.toFixed(MapInterface.COORDINATES_PRECISION)),
                currentLinePoint.elevation,
                deltaDistance
            ].join(',') + ';';
        }

        return serializeString;
    },

    resetRidesTracksStyle() {
        this._rideTrackModel.rideFeatures = [];
        this._rideTrackModel.updatedRideIds = this._rideTrackModel.ridesData.map((ride) => ride.rideId);

        this._removeRideDirectionArrows();
        this._updateRideDirectionArrows();

        this._updateRideFinishMarkers();

        this._drawRideTracksAndMarkers();
    },

    clearRidesTracks() {
        this._removeRideDirectionArrows();

        for (let trackIndex = 0; trackIndex < this._rideTrackModel.rideFeatures.length; trackIndex++) {
            if (this._rideTrackModel.rideFeatures[trackIndex].polylineSourceId) {
                this.resetMapSource(this._rideTrackModel.rideFeatures[trackIndex].polylineSourceId, true);
            }
        }

        this._rideTrackModel.ridesData = [];
        this._rideTrackModel.rideFeatures = [];
        this._rideTrackModel.updatedRideIds = [];

        this._rideTrackModel.directionArrows = {};

        this._model.pointsByDistanceTraveled = {};

        this._updateRideFinishMarkers();
    },

    /* Private Methods
    ******************** */
    _onRideTrackZoomChange() {
        this._model.map.on('zoomend', () => {
            if (!this.isDataChangedOnZoom(this._model.map.getZoom(), this._model.previousZoom)) {
                return;
            }

            this._drawRideTrackDirectionArrows();
        });
    },

    _fetchRidesTracks(params, doneCallback) {
        const requestParam = {
            filter: {}
        };

        if (this._rideTrackModel.onlyOneTrack) {
            // Check if already downloaded
            const currentRideFeature = this._rideTrackModel.rideFeatures.find((rideFeature) => rideFeature.rideId === this._rideTrackModel.rideId);
            if (currentRideFeature && currentRideFeature.linePoints.length > 2) {
                this._rideTrackModel.hasRideData = true;

                // Draw data
                this._drawTracksAndMarkers();

                if (typeof doneCallback === 'function') {
                    doneCallback();
                }

                return;
            }

            this._rideTrackModel.hasRideData = false;

            requestParam.filter = {
                rideId: this._rideTrackModel.rideId
            };
        }

        if (params && Utils.isPresent(params.rideIds)) {
            requestParam.filter.rideIds = params.rideIds;
        }

        api.get('/api/v1/rides/complete-tracks', requestParam)
            .then((ridesData) => {
                if (!ridesData || !ridesData.rides) {
                    return;
                }

                this._rideTrackModel.updatedRideIds = [];
                ridesData.rides.forEach((newRide) => {
                    if (this._rideTrackModel.onlyOneTrack) {
                        this._rideTrackModel.hasRideData = true;
                    }

                    this._rideTrackModel.ridesData = this._rideTrackModel.ridesData.replaceOrAdd('rideId', newRide);

                    this._rideTrackModel.updatedRideIds.push(newRide.rideId);
                });

                if (this._rideTrackModel.ridesData.length > 0) {
                    // Draw data
                    this._drawRideTracksAndMarkers();
                } else {
                    this.clearRidesTracks();
                }

                if (typeof doneCallback === 'function') {
                    doneCallback();
                }
            });
    },

    _drawRideTracksAndMarkers() {
        // establish key points to edit with
        this._setupRideTracks();

        // draw trip polyline, add to map
        this._redrawRideTracks();

        if (this._rideTrackModel.showRideMarker) {
            // draw markers
            this._redrawRideTrackMarkers();
        }

        this._drawRideTrackDirectionArrows();
    },

    _setupRideTracks() {
        if (Utils.isEmpty(this._rideTrackModel.ridesData)) {
            // Empty set of tracks
            return;
        }

        for (let trackIndex = 0; trackIndex < this._rideTrackModel.ridesData.length; trackIndex++) {
            if (Utils.isEmpty(this._rideTrackModel.ridesData[trackIndex])) {
                continue;
            }

            const currentRideId = this._rideTrackModel.ridesData[trackIndex].rideId || this._rideTrackModel.ridesData[trackIndex].id;

            // Check if ride is already loaded
            if (!this._rideTrackModel.updatedRideIds.includes(currentRideId)) {
                continue;
            }

            // Ride doesn't exist yet
            const newEncodedPolyline = this._rideTrackModel.ridesData[trackIndex].encodedPolyline || this._rideTrackModel.ridesData[trackIndex].simplifiedPolyline;
            const lineTracksPoints = [];
            if (Utils.isPresent(newEncodedPolyline)) {
                // Decode polyline to convert in [lat, lng] format
                const trackPolyline = Polyline.decode(newEncodedPolyline);
                for (let trackCoord = 0; trackCoord < trackPolyline.length; trackCoord++) {
                    lineTracksPoints.push(
                        new MapInterface.PointInfo(
                            {
                                lat: trackPolyline[trackCoord][0],
                                lng: trackPolyline[trackCoord][1]
                            },
                            null
                        )
                    );
                }
            }

            const currentTrackFeature = {
                ...this._rideTrackModel.ridesData[trackIndex],
                rideId: currentRideId,
                linePoints: lineTracksPoints,
                defaultPicture: this._rideTrackModel.ridesData[trackIndex].defaultPicture?.mini?.jpg,
                linkName: I18n.t('js.maps.marker.ride.details'),
                rideType: this._rideTrackModel.ridesData[trackIndex].rideType.toLowerCase()
            };

            if (this._rideTrackModel.useSecondaryLine) {
                currentTrackFeature.polylineSourceId = this.addMapTrackLine(this._model.mapTrackLineName + '-' + currentTrackFeature.rideId, this._model.mapSecondaryLineProps);
            } else {
                currentTrackFeature.polylineSourceId = this.addMapTrackLine(this._model.mapTrackLineName + '-' + currentTrackFeature.rideId);
            }

            this._rideTrackModel.rideFeatures.push(currentTrackFeature);
        }
    },

    _redrawRideTracks() {
        this._model.pointsByDistanceTraveled = {};

        for (let trackIndex = 0; trackIndex < this._rideTrackModel.rideFeatures.length; trackIndex++) {
            const currentRideId = this._rideTrackModel.rideFeatures[trackIndex].rideId;

            // Check if ride is already loaded
            if (!this._rideTrackModel.updatedRideIds.includes(currentRideId)) {
                continue;
            }

            // Not used for now
            // if (this._rideTrackModel.currentHighlightedRide && this._rideTrackModel.currentHighlightedRide.rideId !== this._rideTrackModel.rideFeatures[trackIndex].rideId) {
            //     continue;
            // }

            const linePoints = this._rideTrackModel.rideFeatures[trackIndex].linePoints;
            if (linePoints.length < 3) {
                continue;
            }

            // set initial 'total Distance' variable
            let totalDistance = 0;

            // what distance marker we are currently on
            // let markerCount = 0;

            // draw this line segment
            const nextLine = [];
            for (let pointIndex = 0; pointIndex <= linePoints.length - 1; pointIndex++) {
                nextLine.push(linePoints[pointIndex].latLng);

                const thisLinePointLatLng = linePoints[pointIndex].latLng;
                let prevLinePointLatLng;

                if (pointIndex !== 0) {
                    prevLinePointLatLng = linePoints[pointIndex - 1].latLng;
                    const thisLegDist = Utils.flooredNum(this.distHaversine(prevLinePointLatLng, thisLinePointLatLng), MapInterface.COORDINATES_PRECISION);
                    totalDistance = Utils.flooredNum(totalDistance + thisLegDist, MapInterface.COORDINATES_PRECISION);
                }

                this._model.pointsByDistanceTraveled[Math.floor(totalDistance)] = thisLinePointLatLng;
            }

            // draw lines on map in the set color
            const thisLine = {
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: nextLine.map((lngLat) => [lngLat.lng, lngLat.lat])
                }
            };
            this.updateSource(this._rideTrackModel.rideFeatures[trackIndex].polylineSourceId, thisLine);

            // Add finish marker to the end point
            this._rideTrackModel.rideFeatures[trackIndex].endPoint = linePoints[linePoints.length - 1];
            this._updateRideFinishMarkers();
        }
    },

    _redrawRideTrackMarkers() {
        let rideType;
        let startLinePoint;
        let currentRideId;
        const mapMarkers = {};

        for (let trackIndex = 0; trackIndex < this._rideTrackModel.rideFeatures.length; trackIndex++) {
            const linePoints = this._rideTrackModel.rideFeatures[trackIndex].linePoints;

            if (linePoints.length > 0) {
                startLinePoint = linePoints[0];
                currentRideId = this._rideTrackModel.rideFeatures[trackIndex].rideId;

                rideType = this._rideTrackModel.rideFeatures[trackIndex].rideType || DefaultRideType;
                startLinePoint.keyPoint = {
                    type: 'Feature',
                    properties: {
                        rideId: currentRideId,
                        markerFeatureIndex: trackIndex
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [startLinePoint.latLng.lng, startLinePoint.latLng.lat]
                    }
                };
                mapMarkers[rideType] = (mapMarkers[rideType] || []).concat(startLinePoint.keyPoint);
            }
        }

        Object.keys(mapMarkers)
            .forEach((type) => {
                this.updateSource(type + '-primary-marker', {
                    type: 'FeatureCollection',
                    features: mapMarkers[type]
                });
            });
    },

    _drawRideTrackDirectionArrows() {
        // how often SHOULD we be placing direction arrows?
        const directionArrowInterval = this.getZoomDictionary(this._model.map.getZoom()).directionArrow;
        if (!directionArrowInterval) {
            this._removeRideDirectionArrows();

            return;
        }

        for (let trackIndex = 0; trackIndex < this._rideTrackModel.rideFeatures.length; trackIndex++) {
            const currentRideId = this._rideTrackModel.rideFeatures[trackIndex].rideId;
            const currentTrack = this._rideTrackModel.rideFeatures[trackIndex].linePoints;
            if (currentTrack.length < 3) {
                return;
            }

            // Check if ride is already loaded
            if (!this._rideTrackModel.updatedRideIds.includes(currentRideId)) {
                continue;
            }

            this._rideTrackModel.directionArrows[currentRideId] = [];

            // draw a fake point in between every key point to allow user to insert a new point here.
            // variable for distance since last direction arrow
            let distanceSinceLastDirectionArrow = 0;

            // after placing a direction arrow, how much distance is left over (so we can place correctly after the next invisible point)
            let carryoverDistance = 0;
            let lastKeyPointIndex = 0;
            for (let linePointIndex = 0; linePointIndex < currentTrack.length; linePointIndex++) {
                const thisLinePoint = currentTrack[linePointIndex];
                let prevLinePoint;

                // variable for point-to-point distance
                let distanceBetween = 0;
                if (linePointIndex > 0) {
                    prevLinePoint = currentTrack[linePointIndex - 1];
                    distanceBetween = this.distHaversine(prevLinePoint.latLng, thisLinePoint.latLng, MapInterface.COORDINATES_PRECISION);
                    distanceSinceLastDirectionArrow += distanceBetween;
                }

                // if we've traveled more than the direction arrow interval, find out how many to place
                let directionArrowsToPlace = 0;
                if (distanceSinceLastDirectionArrow > directionArrowInterval) {
                    directionArrowsToPlace = Math.floor(distanceSinceLastDirectionArrow / directionArrowInterval);
                }

                // start with carryover distance
                // move from point A to point B, placing direction arrows at interval specified (remove carryover from last loop iteration)
                // when done, set carryover distance and continue
                if (directionArrowsToPlace > 0) {
                    for (let directionArrowIndex = 1; directionArrowIndex <= directionArrowsToPlace; directionArrowIndex++) {
                        const distanceToNextDirectionArrow = (directionArrowInterval * directionArrowIndex) - carryoverDistance;
                        const percentageBetweenPoints = distanceToNextDirectionArrow / distanceBetween;

                        // we now know at what percentage distance the direction arrow should be placed. Use to put it on the line
                        const directionArrowLat = prevLinePoint.latLng.lat + ((thisLinePoint.latLng.lat - prevLinePoint.latLng.lat) * percentageBetweenPoints);
                        const directionArrowLng = prevLinePoint.latLng.lng + ((thisLinePoint.latLng.lng - prevLinePoint.latLng.lng) * percentageBetweenPoints);

                        const diffLat = thisLinePoint.latLng.lat - prevLinePoint.latLng.lat;
                        const diffLng = thisLinePoint.latLng.lng - prevLinePoint.latLng.lng;
                        // const center = [thisLinePoint.latLng.lat + diffLat / 2, thisLinePoint.latLng.lng + diffLng / 2];
                        const angle = (360 - ((Math.atan2(diffLat, diffLng) * 57.295779513082) % 360));

                        // create direction arrow object
                        const newDirectionArrow = new MapInterface.DirectionArrow(
                            {
                                type: 'Feature',
                                properties: {
                                    angle: angle,
                                    linePointIndex: linePointIndex,
                                    directionArrowIndex: directionArrowIndex
                                },
                                geometry: {
                                    type: 'Point',
                                    coordinates: [directionArrowLng, directionArrowLat]
                                }
                            },
                            lastKeyPointIndex
                        );

                        // add to array for reference
                        this._rideTrackModel.directionArrows[currentRideId].push(newDirectionArrow);

                        if (directionArrowIndex === directionArrowsToPlace) {
                            // last need
                            carryoverDistance = distanceBetween - (percentageBetweenPoints * distanceBetween);
                            distanceSinceLastDirectionArrow = carryoverDistance;
                        }
                    }
                } else {
                    carryoverDistance += distanceBetween;
                }

                if (thisLinePoint.keyPoint !== null) {
                    lastKeyPointIndex = linePointIndex;
                }
            }
        }

        this._updateRideDirectionArrows();
    },

    _updateRideDirectionArrows() {
        if (Object.keys(this._rideTrackModel.directionArrows).length) {
            this.updateSource('arrow-marker', {
                type: 'FeatureCollection',
                features: Object.values(this._rideTrackModel.directionArrows)
                    .flat()
                    .map((directionArrows) => directionArrows.keyPoint)
            });

            this.updateSource('finish-marker', {
                type: 'FeatureCollection',
                features: this._rideTrackModel.rideFeatures
                    .filter((feature) => !!feature.endPoint)
                    .map((feature) => ({
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [feature.endPoint.latLng.lng, feature.endPoint.latLng.lat]
                        }
                    }))
            });
        } else {
            this.resetMapSource('arrow-marker');
        }
    },

    _removeRideDirectionArrows() {
        this._rideTrackModel.directionArrows = {};

        this.resetMapSource('arrow-marker');
    },

    _removeRideTracks(rideId) {
        this._rideTrackModel.ridesData = this._rideTrackModel.ridesData.filter((data) => data.rideId !== rideId);

        this._rideTrackModel.rideFeatures = this._rideTrackModel.rideFeatures.filter((feature) => feature.rideId !== rideId);

        delete this._rideTrackModel.directionArrows[rideId];

        const mapSourceId = this._model.mapTrackLineName + '-' + rideId;
        if (this.hasSourceData(mapSourceId)) {
            this.resetMapSource(mapSourceId);

            this._updateRideFinishMarkers();

            this._updateRideDirectionArrows();
        }
    },

    _updateRideFinishMarkers() {
        if (this._rideTrackModel.rideFeatures.length) {
            this.updateSource('finish-marker', {
                type: 'FeatureCollection',
                features: this._rideTrackModel.rideFeatures
                    .filter((feature) => !!feature.endPoint)
                    .map((feature) => ({
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [feature.endPoint.latLng.lng, feature.endPoint.latLng.lat]
                        }
                    }))
            });
        } else {
            this.resetMapSource('finish-marker');
        }
    }
};

export default RideTrackMapModule;
